import React from 'react';
import { Formik } from 'formik';

import { STATUSES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';
import { getLessonStatusContextItems } from 'helpers/getLessonStatusContextItems';
import { getReadonlyLessonText } from 'helpers/getReadonlyLessonText';
import { isLessonSaveVisible } from 'helpers/isLessonSaveVisible';

import { AutoSave } from 'components/AutoSave';
import { LessonLayout } from 'components/Layout';
import { LessonHeader } from 'components/LessonHeader';
import { LessonLocaleTabs } from 'components/LessonLocaleTabs';
import { Button } from 'components/shared/Button';
import { Loading } from 'components/shared/Loading';
import { TheoryBlocks } from 'components/TheoryBlocks';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';
import { BreadcrumbsToolbar } from 'components/Toolbar';

import {
  convertDataToForm,
  convertDataToApi,
} from './TheoryLessonPage.helpers';

import styles from './TheoryLessonPage.module.scss';

const TheoryLessonPage = ({
  saveLoading,
  autoSaveLoading,
  readOnly,
  courseTitle,
  courseStatus,
  isCatalogLesson,
  baseLesson,
  lesson,
  lastActionAt,
  onAutoSaveSubmit,
  onSaveClick,
  onPreviewClick,
  onRemoveClick,
  onStatusContextItemClick,
  activeLocale,
  availableLocales,
  localesWithContent,
  localeContentLoading,
  lessonType,
  onLocaleChange,
  translateAllContentLoading,
  canTranslateAllContentFromRu,
  onTranslateAllContentFromRu,
}: {
  saveLoading: any;
  autoSaveLoading: any;
  readOnly: any;
  courseTitle: any;
  courseStatus: any;
  isCatalogLesson: any;
  baseLesson: any;
  lesson: any;
  lastActionAt: any;
  onAutoSaveSubmit: any;
  onSaveClick: any;
  onPreviewClick: any;
  onRemoveClick: any;
  onStatusContextItemClick: any;
  activeLocale: string;
  availableLocales: readonly string[];
  localesWithContent: string[];
  localeContentLoading: boolean;
  lessonType: string;
  onLocaleChange: (locale: string) => void;
  translateAllContentLoading: boolean;
  canTranslateAllContentFromRu: boolean;
  onTranslateAllContentFromRu: () => Promise<void>;
}) => {
  const shellLesson = baseLesson;

  if (localeContentLoading || !lesson) {
    return (
      <LessonLayout
        sidebarComponent={<div />}
        toolbarComponent={
          <BreadcrumbsToolbar
            breadcrumbsData={{
              courseTitle,
              lessonTitle: getTranslation('common.text.untitled'),
            }}
            endAdornment={
              <ToolbarActions
                withRemove
                withSave={isLessonSaveVisible({
                  status: shellLesson.status,
                  activeLocale,
                })}
                saveLoading={saveLoading}
                autoSaveLoading={autoSaveLoading}
                contextItems={getLessonStatusContextItems({
                  courseReadOnly:
                    isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                  status: shellLesson.status,
                })}
                removeData={{
                  id: shellLesson.id,
                  title: shellLesson.title,
                }}
                updatedAt={lastActionAt}
                status={shellLesson.status}
                onSaveClick={() => {
                  return;
                }}
                onPreviewClick={onPreviewClick}
                onRemoveClick={onRemoveClick}
                onStatusContextItemClick={() => {
                  return;
                }}
              />
            }
          />
        }
      >
        <div className={styles.page}>
          {readOnly && (
            <ReadOnlySection text={getReadonlyLessonText(shellLesson.status)} />
          )}
          <LessonLocaleTabs
            lessonId={shellLesson.id}
            lessonType={lessonType}
            activeLocale={activeLocale}
            availableLocales={availableLocales}
            localesWithContent={localesWithContent}
            onLocaleChange={onLocaleChange}
          />
          <div className={styles.inner}>
            <Loading />
          </div>
        </div>
      </LessonLayout>
    );
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={convertDataToForm(lesson, readOnly)}
      onSubmit={onAutoSaveSubmit}
    >
      {({ values }: { values: any }) => (
        <LessonLayout
          sidebarComponent={<div />}
          toolbarComponent={
            <BreadcrumbsToolbar
              breadcrumbsData={{ courseTitle, lessonTitle: values.title }}
              endAdornment={
                <ToolbarActions
                  withRemove
                  withSave={isLessonSaveVisible({
                    status: shellLesson.status,
                    activeLocale,
                  })}
                  saveLoading={saveLoading}
                  autoSaveLoading={autoSaveLoading}
                  contextItems={getLessonStatusContextItems({
                    courseReadOnly:
                      isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                    status: lesson.status,
                  })}
                  removeData={{ id: lesson.id, title: lesson.title }}
                  updatedAt={lastActionAt}
                  status={lesson.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onRemoveClick={onRemoveClick}
                  onStatusContextItemClick={(contextItem: any) =>
                    onStatusContextItemClick(
                      contextItem,
                      convertDataToApi(values),
                    )
                  }
                />
              }
            />
          }
        >
          <div className={styles.page}>
            <AutoSave debounceMs={1000} />
            {readOnly && (
              <ReadOnlySection text={getReadonlyLessonText(lesson.status)} />
            )}
            <LessonHeader readOnly={readOnly} lessonRole={lesson.role} />
            <div className={styles.localeRow}>
              <LessonLocaleTabs
                lessonId={lesson.id}
                lessonType={lessonType}
                activeLocale={activeLocale}
                availableLocales={availableLocales}
                localesWithContent={localesWithContent}
                onLocaleChange={onLocaleChange}
              />
              {activeLocale === 'en' && (
                <Button
                  className={styles.translateAllContentBtn}
                  size="sm"
                  variant="outlined"
                  disabled={
                    !canTranslateAllContentFromRu || translateAllContentLoading
                  }
                  startIcon={
                    translateAllContentLoading && (
                      <Loading size={18} color="var(--color-primary)" />
                    )
                  }
                  onClick={onTranslateAllContentFromRu}
                >
                  {getTranslation('common.button.translate_all_content_from_ru')}
                </Button>
              )}
            </div>
            <div className={styles.inner}>
              <TheoryBlocks namePrefix="blocks" />
            </div>
          </div>
        </LessonLayout>
      )}
    </Formik>
  );
};

export default React.memo(TheoryLessonPage) as any;
